<template>
  <div id="app">
    <!-- 登录页面 -->
    <Login v-if="!isLoggedIn" @login-success="handleLoginSuccess" />
    
    <!-- 主后台管理系统 -->
    <div v-else class="admin-layout">
      <!-- 左侧边栏 -->
      <div class="sidebar">
        <div class="sidebar-header">
          <div class="logo">
            <div class="logo-icon">肾</div>
            <span class="logo-text">肾数科技</span>
          </div>
        </div>
        
        <nav class="sidebar-nav">
          <div v-for="route in filteredMenuRoutes" :key="route.path" class="nav-group">
            <!-- 有子项的父菜单 -->
            <div v-if="route.children && route.children.length" class="nav-parent">
              <div class="nav-item-content parent" @click="toggleExpand(route.name)">
                <el-icon><component :is="route.meta.icon" /></el-icon>
                <span>{{ route.meta.title }}</span>
                <el-icon class="caret" v-if="expanded[route.name]"><ArrowUp /></el-icon>
                <el-icon class="caret" v-else><ArrowDown /></el-icon>
              </div>
              <transition name="nav-collapse">
                <div class="nav-children" v-show="expanded[route.name]">
                  <router-link
                    v-for="child in route.children"
                    :key="child.path"
                    :to="`${route.path}/${child.path}`"
                    class="nav-sub-item"
                    custom
                    v-slot="{ navigate }"
                  >
                    <div class="nav-item-content" @click="navigate">
                      <el-icon><component :is="child.meta.icon" /></el-icon>
                      <span>{{ child.meta.title }}</span>
                    </div>
                  </router-link>
                </div>
              </transition>
            </div>

            <!-- 无子项的普通菜单 -->
            <router-link v-else :key="route.path" :to="route.path" class="nav-item" custom v-slot="{ navigate }">
              <div class="nav-item-content" @click="navigate">
                <el-icon><component :is="route.meta.icon" /></el-icon>
                <span>{{ route.meta.title }}</span>
              </div>
            </router-link>
          </div>
        </nav>
      </div>

      <!-- 主内容区域 -->
      <div class="main-content">
        <!-- 顶部头部 -->
        <header class="header">
          <div class="header-right">
            <el-icon class="header-icon"><Search /></el-icon>
            <el-icon class="header-icon"><Bell /></el-icon>
            <div class="user-info" @click="goToUserInfo">
              <el-icon><Avatar /></el-icon>
              <span>{{ currentUser.userName || '用户名' }}</span>
            </div>
            <el-icon class="header-icon" @click="handleLogout"><SwitchButton /></el-icon>
          </div>
        </header>

        <!-- 内容区域 -->
        <div class="content">
          <router-view />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import Login from './Login.vue'

const router = useRouter()
const isLoggedIn = ref(false)
const currentUser = ref({})
const isAdmin = ref(false)

const userRole = ref(localStorage.getItem('roles'))

// 菜单路由配置：使用 router.menuRoutes 保留 children 结构以支持多级菜单
const menuRoutes = computed(() => {
  const raw = router.menuRoutes || []
  return raw.filter(route => route.meta && route.meta.title && route.path !== '/' && !route.meta.hidden)
})

// 根据用户角色过滤菜单路由（保留 children）
const filteredMenuRoutes = computed(() => {
  return menuRoutes.value
    .map(route => {
      // 复制 route 对象以避免直接修改原始 routes
      const r = { ...route }
      if (r.children && r.children.length) {
        r.children = r.children.filter(child => child.meta && !child.meta.hidden && (!child.meta.roles || child.meta.roles.includes(userRole.value)))
      }
      return r
    })
    .filter(route => {
      // 如果父路由自身有 roles 限制，保留检查
      if (route.meta && route.meta.roles && !route.meta.roles.includes(userRole.value)) return false
      // 如果是没有子项的父项，直接显示；如果有子项但子项为空，则隐藏
      if (route.children && route.children.length === 0 && !route.component) return false
      return true
    })
})

// 管理展开状态（用于多级菜单展开/收起）
const expanded = ref({})
const toggleExpand = (name) => {
  const willOpen = !expanded.value[name]
  expanded.value = {} // 折叠其他一级菜单
  if (willOpen) {
    expanded.value[name] = true
  }
}

const visibleChildren = (route) => {
  return (route.children || []).filter(child => child.meta && !child.meta.hidden && (!child.meta.roles || child.meta.roles.includes(userRole.value)))
}

// 从 localStorage 加载用户信息
const loadUserInfo = () => {
  const userInfo = localStorage.getItem('userInfo')
  if (userInfo) {
    try {
      currentUser.value = JSON.parse(userInfo)
    } catch (error) {
      console.error('解析用户信息失败:', error)
    }
  }
  
  // 检查用户角色
  const roles = localStorage.getItem('roles')
  isAdmin.value = roles === 'admin'
  userRole.value = roles // 更新用户角色
}

// 检查登录状态
const checkLoginStatus = () => {
  const token = localStorage.getItem('token')
  if (token) {
    isLoggedIn.value = true
    loadUserInfo()
  }
}

const handleLoginSuccess = () => {
  isLoggedIn.value = true
  loadUserInfo()
  ElMessage.success('登录成功，欢迎使用肾数科技后台管理系统！')
  // 登录成功后跳转到控制面板
  
  if(userRole.value === 'admin') {
    router.push('/patients')
  } else {
    router.push('/knowledge')
  }
}

const handleLogout = () => {
  ElMessageBox.confirm(
    '确定要退出登录吗？',
    '提示',
    {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'info',
    }
  ).then(() => {
    isLoggedIn.value = false
    currentUser.value = {}
    isAdmin.value = false
    userRole.value = null // 清空用户角色
    localStorage.removeItem('token')
    localStorage.removeItem('userInfo')
    localStorage.removeItem('roles')
    ElMessage.success('已退出登录')
    // 退出后跳转到登录页
    router.push('/')
  }).catch(() => {
    // 用户取消退出
  })
}

// 跳转到个人信息页面
const goToUserInfo = () => {
  router.push('/user-info')
}

// 组件挂载时检查登录状态
onMounted(() => {
  checkLoginStatus()
})
</script>

<style lang="css">
.logo-text{
  color: white !important;
}
.sidebar-header{
  padding: 8px 20px;
}
.logo-icon{
  margin-left: 20px;
}
.logo-icon{
  width: 40px;
  height: 40px;
  position: relative;
}

/* 添加用户名点击样式 */
.user-info {
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 8px 12px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.user-info:hover {
  background-color: rgba(255, 255, 255, 0.1);
  transform: translateY(-1px);
}


/* 菜单样式（支持二级菜单） */
.sidebar-nav { padding: 12px; display:flex; flex-direction:column; gap:6px }
.nav-group { display:flex; flex-direction:column }
.nav-item, .nav-parent { display:block; text-decoration:none; padding:12px 18px; box-sizing: border-box;}
.nav-item-content { display:flex; align-items:center; gap:10px; padding:10px 12px; border-radius:8px; cursor:pointer }
.nav-item-content:hover { background: rgba(255,255,255,0.03) }
.nav-item-content.parent { align-items:center }
.nav-item-content .caret { margin-left: auto; font-size: 14px; color: rgba(255,255,255,0.45) }
.nav-children { padding-left: 12px; display:flex; flex-direction:column; gap:4px; overflow:hidden }
.nav-sub-item .nav-item-content { padding:8px 12px; font-size:14px }
.nav-collapse-enter-active,
.nav-collapse-leave-active { transition: max-height 0.3s ease, opacity 0.2s ease; }
.nav-collapse-enter-from,
.nav-collapse-leave-to { max-height: 0; opacity: 0; }
.nav-collapse-enter-to,
.nav-collapse-leave-from { max-height: 600px; opacity: 1; }


</style>
